import { Alert, Box, Container, Divider, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { LoadingContent } from '../../components/Loading';
import Logo from '../../assets/logo.png';
import { usePublicProposal } from './hooks/usePublicProposal';
import { TermStep } from './steps/TermStep';
import { SignStep } from './steps/SignStep';
import { PlanPaymentStep } from './steps/PlanPaymentStep';
import { CustomizationPaymentStep } from './steps/CustomizationPaymentStep';
import { CreateClubStep } from './steps/CreateClubStep';

export default function PublicProposalPage() {
    const { propostaId } = useParams();

    const {
        contract,
        error,
        mutate,
        isFetching,
        gates,
        canPay,
        planAmount,
        planPaid,
        hasCustomization,
        customAmount,
        customPaid,
        canCreateClub,
        clubCreated,
    } = usePublicProposal(propostaId);

    if (isFetching) return <LoadingContent />;

    if (error && !contract) {
        return (
            <Container sx={{ maxWidth: { md: 800 }, py: 6 }}>
                <Typography color="error">Não foi possível carregar a proposta. Verifique o link e tente novamente.</Typography>
            </Container>
        );
    }

    if (!propostaId || (!isFetching && !contract)) {
        return (
            <Container sx={{ maxWidth: { md: 800 }, py: 6 }}>
                <Typography>Proposta não encontrada.</Typography>
            </Container>
        );
    }

    const showSign = !gates.skipContractSignature;
    // Blocos de pagamento permanecem visíveis após quitados (com status), para o cliente consultar.
    const showPlanPayment = canPay && !gates.skipPlanPayment;
    const showCustomizationPayment =
        canPay &&
        !gates.skipCustomizationPayment &&
        hasCustomization &&
        (gates.skipPlanPayment || planPaid);
    const showCreateClub = canCreateClub || clubCreated;

    return (
        <Container sx={{ maxWidth: { md: 800 }, pb: 6 }}>
            <Stack rowGap={2}>
                <Box component="img" src={Logo} width={200} height="auto" sx={{ mt: 2 }} />

                <Typography variant="h1" component="h1">
                    Proposta de Adesão
                </Typography>
                <Divider />

                <Box>
                    <Typography variant="body2">
                        1. Leia com atenção a proposta comercial e confira se os dados estão de acordo.
                    </Typography>
                    <Typography variant="body2">
                        2. Acesse os termos e condições para uso do software e revise as cláusulas.
                    </Typography>
                    <Typography variant="body2">
                        3. Caso esteja de acordo, preencha os campos abaixo com as informações da sua empresa.
                    </Typography>
                </Box>

                {(gates.skipContractSignature || gates.skipPlanPayment || gates.skipCustomizationPayment) && (
                    <Alert severity="info">
                        Algumas etapas foram dispensadas pelo comercial
                        {[
                            gates.skipContractSignature && 'assinatura do contrato',
                            gates.skipPlanPayment && 'pagamento do plano',
                            gates.skipCustomizationPayment && 'pagamento das personalizações',
                        ]
                            .filter(Boolean)
                            .join(', ')
                            .replace(/, ([^,]+)$/, ' e $1')}
                        .
                    </Alert>
                )}

                <TermStep contract={contract} mutate={mutate} />

                {showSign && <SignStep contract={contract} />}

                {showPlanPayment && (
                    <PlanPaymentStep
                        contract={contract}
                        mutate={mutate}
                        planAmount={planAmount}
                    />
                )}

                {showCustomizationPayment && (
                    <CustomizationPaymentStep
                        contract={contract}
                        mutate={mutate}
                        customAmount={customAmount}
                    />
                )}

                {showCreateClub && (
                    <CreateClubStep
                        contract={contract}
                        mutate={mutate}
                        clubCreated={clubCreated}
                    />
                )}
            </Stack>
        </Container>
    );
}
